// Where a "receive on Jupnet" goes through: automatic (the best quote, the home chain on a near
// tie) or one route the user fixes. Routes that cannot be used from this source say why.

import type { Chain, ChainKey } from "../chains";
import { prettyUnits } from "../format";
import { jupnetBlocked, routeOptions, type JupnetAsset } from "../jupnet-receive";
import { ChainLogo, IconCheck } from "./icons";
import { Modal } from "./Modal";

type Props = {
  asset: JupnetAsset;
  src: Chain;
  /** the route in use, and whether it was picked automatically */
  current: ChainKey;
  auto: boolean;
  /** each route's best quote at the current amount, when they were compared */
  quotes: Partial<Record<ChainKey, bigint | null>>;
  onAuto: () => void;
  onPick: (chain: ChainKey, token: string) => void;
  onClose: () => void;
};

export function RoutePicker({ asset, src, current, auto, quotes, onAuto, onPick, onClose }: Props) {
  const opts = routeOptions(asset, src);
  const blocked = jupnetBlocked(src);
  const best = Object.values(quotes).reduce<bigint | null>((m, v) => (v != null && (m === null || v > m) ? v : m), null);
  return (
    <Modal title={`Route to Jupnet · ${asset.sym}`} onClose={onClose}>
      <p className="picker-lede">A solver deposits {asset.unified ? "USDC" : asset.sym} into gum's bank on the route's chain; Jupnet then credits your account.</p>
      {blocked && (
        <p className="alert warn" role="status">
          {blocked}
        </p>
      )}
      <ul className="token-list" aria-label="Routes">
        <li>
          <button className={`token-row${auto ? " selected" : ""}`} onClick={() => (onAuto(), onClose())} aria-current={auto || undefined} disabled={!!blocked}>
            <span className="route-auto" aria-hidden>
              <IconCheck size={16} />
            </span>
            <span className="token-row-main">
              <span className="token-row-sym">Automatic</span>
              <span className="token-row-sub">The best quote{asset.home ? `, ${opts.find((o) => o.home)?.chain.label ?? "the home chain"} on a near tie` : ""}</span>
            </span>
          </button>
        </li>
        {opts.map((o) => {
          const q = quotes[o.chain.key];
          const on = !auto && o.chain.key === current;
          return (
            <li key={o.chain.key}>
              <button className={`token-row${on ? " selected" : ""}`} disabled={!o.ok} onClick={() => (onPick(o.chain.key, o.token.addr), onClose())} aria-current={on || undefined}>
                <ChainLogo chain={o.chain.key} size={34} />
                <span className="token-row-main">
                  <span className="token-row-sym">
                    Via {o.chain.label}
                    {o.home && <span className="tag">home chain</span>}
                    {auto && o.chain.key === current && <span className="tag">in use</span>}
                  </span>
                  <span className="token-row-sub">{o.ok ? `Deposits ${o.token.sym} on ${o.chain.label}` : o.reason}</span>
                </span>
                {o.ok && q !== undefined && (
                  <span className="token-row-bal">
                    {q === null ? <span className="muted">no quote</span> : `${prettyUnits(q, o.token.decimals)} ${o.token.sym}`}
                    {q !== null && q === best && <span className="tag">best</span>}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="fine">
        {asset.unified
          ? "Every route credits the same unified USD on Jupnet."
          : `Each route credits ${asset.sym} bridged from its chain: a separate Jupnet token per route.`}{" "}
        Sui is not a route: it has no gum bank.
      </p>
    </Modal>
  );
}
